// src/pages/agent/JvDashboard.js
// Internal JV dashboard — same layout as the public /jv-status/:tokenId
// page, but read straight from Firestore (no password prompt). Admin/agent
// can see filter chips, live-computed status/notes, and a collapsible
// "Dead deals" section at the bottom without generating a share link.
import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, onSnapshot, query, where, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAgentAuth } from '../../lib/auth';
import { doc } from 'firebase/firestore';
import { PageHeader, Card, Btn, Field, Input, Pill, BURG, BURG_DK, GOLD, LIGHT, MID, BORDER, CREAM } from '../../components/agent/UI';

const STATUS_COLORS = {
  green:  { bg: '#DCFCE7', fg: '#166534' },
  gold:   { bg: '#FEF3C7', fg: '#78350F' },
  blue:   { bg: '#DBEAFE', fg: '#1E40AF' },
  red:    { bg: '#FEE2E2', fg: '#991B1B' },
  gray:   { bg: '#F3F4F6', fg: '#6B7280' }
};
const statusPill = (color) => {
  const c = STATUS_COLORS[color] || STATUS_COLORS.gray;
  return { display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', background: c.bg, color: c.fg };
};

// Mirrors the server-side computation in getJvStatus + the client mirror
// in Reports.js. Keeping this in sync ensures partner + internal views
// always show the same thing.
function computeJvView(l, expectedByLead) {
  const attempts = Array.isArray(l.outreachAttempts) ? l.outreachAttempts : [];
  const last = attempts[attempts.length - 1] || null;
  const responded = !!l.responded;
  const optedOut = !!l.optedOut;
  const fmt = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  let autoNote = '';
  // Order matters — opt-out overrides any prior reply.
  if (optedOut) autoNote = 'Deal is dead. Seller does not want to be contacted.';
  else if (responded) autoNote = `Seller replied ${fmt(l.respondedAt)}. Follow up ASAP.`;
  else if (last) {
    const on = fmt(last.at);
    autoNote = last.ok
      ? `${last.kind === 'followup' ? 'Follow-up' : 'Initial text'} sent to ${last.phone || 'primary'} on ${on}.`
      : `Number ${last.phone || 'primary'} bounced${on ? ` on ${on}` : ''}. Retrying next number on next batch.`;
  } else if (expectedByLead) {
    autoNote = `Queued for ${fmt(new Date(expectedByLead))}.`;
  } else {
    autoNote = 'Queued for next outreach batch.';
  }
  let autoStatus = 'Queued', autoColor = 'gray';
  if (optedOut) { autoStatus = 'Dead Deal · Not interested'; autoColor = 'red'; }
  else if (responded) { autoStatus = 'Interested · Replied'; autoColor = 'green'; }
  else if (l.outreachStatus === 'no_valid_contact') { autoStatus = 'All numbers exhausted'; autoColor = 'red'; }
  else if (l.lastInitialSentAt) { autoStatus = 'Text sent · Awaiting reply'; autoColor = 'gold'; }
  else if (expectedByLead) { autoStatus = 'Scheduled'; autoColor = 'blue'; }
  return {
    status: l.jvStatusOverride || autoStatus,
    color:  l.jvStatusColorOverride || autoColor,
    notes:  l.jvNotesOverride || autoNote
  };
}

export default function JvDashboard() {
  const { profile } = useAgentAuth();
  const [tag, setTag] = useState('JV');
  const [leads, setLeads] = useState([]);
  const [queueByLead, setQueueByLead] = useState({});
  const [pipelineUid, setPipelineUid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showDead, setShowDead] = useState(false);
  const [partner, setPartner] = useState(null);

  // Load partner name/email — prefer a matching share doc, fall back to
  // settings/outreach.partner so the label appears even before a share
  // link is created.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // First try an active share for this tag.
      try {
        const snap = await getDocs(query(collection(db, 'jvShares'), where('active', '==', true), where('tagFilter', '==', tag), limit(1)));
        if (!cancelled && !snap.empty) {
          const d = snap.docs[0].data();
          if (d.partnerName) { setPartner({ name: d.partnerName, email: d.partnerEmail || null }); return; }
        }
      } catch {}
      // Otherwise fall back to global outreach settings.
      try {
        const s = await getDocs(query(collection(db, 'settings'), where('__name__', '==', 'outreach')));
        if (!cancelled && !s.empty) {
          const p = s.docs[0].data().partner;
          if (p?.name) { setPartner({ name: p.name, email: p.email || null }); return; }
        }
      } catch {}
      if (!cancelled) setPartner(null);
    })();
    return () => { cancelled = true; };
  }, [tag]);

  // Resolve which pipeline to read.
  useEffect(() => {
    if (!profile?.uid) return;
    (async () => {
      let uid = profile.uid;
      if (profile.role !== 'admin') {
        uid = profile.invitedByUid || null;
        if (!uid) {
          try {
            const aSnap = await getDocs(query(collection(db, 'agents'),
              where('role', '==', 'admin'), where('status', '==', 'active'), limit(1)));
            if (!aSnap.empty) uid = aSnap.docs[0].id;
          } catch {}
        }
        if (!uid) uid = profile.uid;
      }
      setPipelineUid(uid);
    })();
  }, [profile?.uid, profile?.role, profile?.invitedByUid]);

  // Live-subscribe to leads with the tag.
  useEffect(() => {
    if (!pipelineUid) return;
    setLoading(true);
    const unsub = onSnapshot(
      query(collection(db, 'agents', pipelineUid, 'leads'), where('tags', 'array-contains', tag)),
      snap => { setLeads(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); },
      () => setLoading(false)
    );
    return unsub;
  }, [pipelineUid, tag]);

  // Live-subscribe to queued outreach (for scheduledFor dates).
  useEffect(() => {
    if (!pipelineUid) return;
    const unsub = onSnapshot(
      query(collection(db, 'outreachQueue'), where('pipelineUid', '==', pipelineUid), where('status', '==', 'queued')),
      snap => {
        const byLead = {};
        snap.docs.forEach(d => {
          const q = d.data();
          const ms = q.scheduledFor?.toMillis?.() || 0;
          if (!byLead[q.leadId] || ms < byLead[q.leadId]) byLead[q.leadId] = ms;
        });
        setQueueByLead(byLead);
      },
      () => setQueueByLead({})
    );
    return unsub;
  }, [pipelineUid]);

  const rows = useMemo(() => {
    // Estimate expected contact date for un-queued, un-contacted leads.
    const pending = leads
      .filter(l => !l.responded && !l.optedOut && (l.outreachStatus || 'pending') === 'pending' && !l.lastInitialSentAt)
      .sort((a, b) => (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0));
    const batchSize = 30; // matches default; ok to be approximate for display
    const expectedByLead = {};
    pending.forEach((l, i) => {
      if (queueByLead[l.id]) {
        expectedByLead[l.id] = queueByLead[l.id];
      } else {
        const dayOffset = Math.floor(i / batchSize) + 1;
        const d = new Date(); d.setDate(d.getDate() + dayOffset); d.setHours(9, 0, 0, 0);
        expectedByLead[l.id] = d.getTime();
      }
    });
    // Contacted-but-not-responded → expected follow-up = contactedAt + 7d.
    leads.forEach(l => {
      if (l.lastInitialSentAt && !l.responded && !l.optedOut) {
        expectedByLead[l.id] = l.lastInitialSentAt.toMillis() + 7 * 86400000;
      }
    });
    return leads.map(l => {
      const owner = (l.owners || [])[0] || {};
      const view = computeJvView(l, expectedByLead[l.id]);
      return {
        id: l.id,
        address: [l.address?.line1, l.address?.city, l.address?.state, l.address?.zip].filter(Boolean).join(', ') || '(no address)',
        ownerName: owner.name || 'Property Owner',
        contacted: !!l.lastInitialSentAt,
        contactedAt: l.lastInitialSentAt?.toDate?.() || null,
        responded: !!l.responded,
        respondedAt: l.respondedAt?.toDate?.() || null,
        expectedAt: expectedByLead[l.id] ? new Date(expectedByLead[l.id]) : null,
        jvStatus: view.status,
        jvColor: view.color,
        notes: view.notes
      };
    });
  }, [leads, queueByLead]);

  const colorOrder = { green: 0, gold: 1, blue: 2, gray: 3, red: 4 };
  const sorted = useMemo(() => [...rows].sort((a, b) => (colorOrder[a.jvColor] ?? 9) - (colorOrder[b.jvColor] ?? 9)), [rows]);

  const filterMatch = (r) => {
    if (filter === 'all') return true;
    if (filter === 'interested') return r.jvColor === 'green';
    if (filter === 'in-progress') return r.jvColor === 'gold' || r.jvColor === 'blue';
    if (filter === 'scheduled') return !r.contacted && !r.responded && r.expectedAt;
    if (filter === 'dead') return r.jvColor === 'red';
    return true;
  };
  const alive = sorted.filter(r => r.jvColor !== 'red' && filterMatch(r));
  const dead = sorted.filter(r => r.jvColor === 'red' && filterMatch(r));
  const counts = {
    all: rows.length,
    interested: rows.filter(r => r.jvColor === 'green').length,
    'in-progress': rows.filter(r => r.jvColor === 'gold' || r.jvColor === 'blue').length,
    scheduled: rows.filter(r => !r.contacted && !r.responded && r.expectedAt).length,
    dead: rows.filter(r => r.jvColor === 'red').length
  };

  const fmt = (d) => d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

  return (
    <>
      <PageHeader
        eyebrow="Dashboard"
        title="JV outreach dashboard"
        sub={partner
          ? `Everything ${partner.name}${partner.email ? ` (${partner.email})` : ''} sees, in real time. Change status per lead from the lead's detail page.`
          : "Everything your JV partner sees, in real time. Change status per lead from the lead's detail page."}
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Field label="Tag"><Input value={tag} onChange={e => setTag(e.target.value)} style={{ width: 100 }} /></Field>
          </div>
        }
      />

      <Card padded={false} style={{ marginBottom: 20 }}>
        <div style={{ padding: '14px 18px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'All' },
            { key: 'interested', label: 'Interested' },
            { key: 'in-progress', label: 'In progress' },
            { key: 'scheduled', label: 'Scheduled' },
            { key: 'dead', label: 'Dead' }
          ].map(f => (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)}
              style={{
                padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                fontFamily: "'Inter', sans-serif",
                border: `1px solid ${filter === f.key ? BURG : BORDER}`,
                background: filter === f.key ? BURG : '#fff',
                color: filter === f.key ? '#fff' : BURG_DK
              }}>
              {f.label} <span style={{ opacity: 0.7 }}>{counts[f.key] || 0}</span>
            </button>
          ))}
        </div>
      </Card>

      {loading ? (
        <Card><div style={{ color: LIGHT, padding: 24, textAlign: 'center' }}>Loading…</div></Card>
      ) : (
        <>
          <RowsTable rows={alive} fmt={fmt} emptyLabel={filter === 'all' ? 'No leads tagged yet.' : 'Nothing matches this filter.'} />

          {dead.length > 0 && (
            <div style={{ marginTop: 24, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, overflow: 'hidden' }}>
              <button type="button" onClick={() => setShowDead(v => !v)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '14px 18px', background: '#FAFAFA', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: "'Inter', sans-serif" }}>
                <span style={{ ...statusPill('red'), marginRight: 10 }}>Dead deals</span>
                <span style={{ color: MID, fontSize: 13 }}>{dead.length} archived — seller opted out, dead-ended, or all numbers exhausted</span>
                <span style={{ marginLeft: 'auto', fontSize: 16, color: MID }}>{showDead ? '▾' : '▸'}</span>
              </button>
              {showDead && <RowsTable rows={dead} fmt={fmt} muted />}
            </div>
          )}
        </>
      )}
    </>
  );
}

function RowsTable({ rows, fmt, muted = false, emptyLabel }) {
  if (rows.length === 0) return <Card><div style={{ color: LIGHT, padding: 30, textAlign: 'center' }}>{emptyLabel || 'Nothing here.'}</div></Card>;
  return (
    <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 14, overflow: 'hidden', opacity: muted ? 0.85 : 1 }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: "'Inter', sans-serif" }}>
          <thead>
            <tr>
              <th style={th}>Address</th>
              <th style={th}>Owner</th>
              <th style={th}>Status</th>
              <th style={th}>Notes (visible to partner)</th>
              <th style={th}>Contacted</th>
              <th style={th}>Expected next</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td style={td}>{r.address}</td>
                <td style={td}>{r.ownerName}</td>
                <td style={td}><span style={statusPill(r.jvColor)}>{r.jvStatus}</span></td>
                <td style={{ ...td, fontSize: 12, color: '#4B5563', maxWidth: 320, lineHeight: 1.5 }}>{r.notes}</td>
                <td style={td}>{r.contactedAt ? fmt(r.contactedAt) : (r.contacted ? 'Yes' : 'No')}</td>
                <td style={td}>{r.expectedAt ? fmt(r.expectedAt) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
const th = { padding: '14px 16px', textAlign: 'left', background: '#FAFAFA', borderBottom: `1px solid ${BORDER}`, fontSize: 11, fontWeight: 600, color: '#6B7280' };
const td = { padding: '14px 16px', borderBottom: `1px solid ${BORDER}`, color: '#374151' };
